import { IconButton, IconButtonType, Spinner } from "@whitespace/whitespace-ui";
import {
  Card, Col, Row, Badge
} from "react-bootstrap";

import {
  ColumnDirective,
  ColumnsDirective,
  FilterSettingsModel, GridComponent, Inject, PageSettingsModel, Resize, Page as Paging
} from "@syncfusion/ej2-react-grids";
import { parseUsingParseInt } from "helpers/stringUtils";
import { makeGridAACompliant } from "modules/SyncfusionAAFixer";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStateMachine } from "little-state-machine";
import AuthorisedVisitorsTemporaryWorkersProps from "interfaces/SiteDiary/AuthorisedTemporaryWorkers/AuthorisedVisitorsTemporaryWorkersProps";
import { RolesEnum } from "modules/rolesEnum";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import { convertToDateFromLocalDate } from "helpers/DateUtils";
import InductedTemplate from "./Templates/InductedTemplate";
import EditTemplate from "./Templates/EditTemplate";

const AuthorisedVisitorsTemporaryWorkers = ({
  diarySaved, authorisedVisitorsTemporaryWorkersDetails, hwrcSiteId, diaryDate
}
: AuthorisedVisitorsTemporaryWorkersProps) => {
  const { state } = useStateMachine();
  const navigate = useNavigate();

  const pageSize: number = parseUsingParseInt(process.env.REACT_APP_DIARY_CARD_PAGE_SIZE);
  const pageSettings: PageSettingsModel = {
    pageSize,
    pageSizes: [5, 10, 20, 50, "All"]
  };

  const addUrl = `${RoutePathsEnum.AuthorisedVisitorTemporaryWorkerAdd}${hwrcSiteId && diaryDate
    ? `/${hwrcSiteId}/${diaryDate}` : ""}`;

  const filterSettings: FilterSettingsModel = { type: "Excel" };
  let grid: GridComponent | undefined;

  const inductedTemplateInstance = InductedTemplate;
  const editTemplateInstance = EditTemplate;

  const [renderingComplete, setRenderingComplete] = useState(false);
  const [isGridRendered, setIsGridRendered] = useState(false);
  const [showSpinner, setShowSpinner] = useState<boolean>(true);

  useEffect(() => {
    if (isGridRendered && renderingComplete) {
      setShowSpinner(false);
    }
  }, [isGridRendered, renderingComplete, setShowSpinner]);

  const gridCreated = () => {
    setIsGridRendered(true);
  };

  const now = new Date();

  const canAmendDiary = diarySaved
  && !(diaryDate && convertToDateFromLocalDate(diaryDate).toLocaleDateString() !== now.toLocaleDateString() && state.userRole === RolesEnum.Site);

  return (
    <Card>
      <Card.Header>
        <Row>
          <Col xs={8} className="header">
            <h3 data-testid="header">
              Authorised Visitors / Temporary Workers
              <Spinner
                animation="grow"
                visible={showSpinner}
                spinnerText="Loading"
                variant="info"
              />
            </h3>
          </Col>
          <Col xs={4} className="right-pane">
            <div className="d-flex">
              {canAmendDiary && (
                <IconButton
                  data-testid="addButtonAuthorisedVisitor"
                  className="me-2"
                  iconType={IconButtonType.Add}
                  onClick={() => navigate(addUrl)}
                >
                  Add
                </IconButton>
              )}
              <Badge data-testid="authorisedVisitorRowCounter">{authorisedVisitorsTemporaryWorkersDetails.length}</Badge>
            </div>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body className="no-padding">
        <Row className="mb-2 ms-2 mt-2 me-2 w-auto">
          <Col>
            <GridComponent
              ref={(g: GridComponent) => {
                grid = g;
              }}
              id="gridAuthorisedVisitorsTemporaryWorkers"
              data-testid="gridAuthorisedVisitorsTemporaryWorkers"
              allowPaging
              allowResizing
              pageSettings={pageSettings}
              filterSettings={filterSettings}
              allowSelection={false}
              queryString=""
              dataSource={diarySaved && authorisedVisitorsTemporaryWorkersDetails}
              actionComplete={() => {
                makeGridAACompliant(grid!, () => {
                  setRenderingComplete(true);
                });
              }}
              created={gridCreated}
              loadingIndicator={{ indicatorType: "Shimmer" }}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="id"
                  headerText="id"
                  visible={false}
                  isPrimaryKey
                />
                <ColumnDirective
                  field="name"
                  data-testid="name"
                  headerText="Name"
                />
                <ColumnDirective
                  field="organisation"
                  data-testid="organisation"
                  headerText="Organisation"
                />
                <ColumnDirective
                  field="inducted"
                  data-testid="inducted"
                  headerText="Inducted"
                  textAlign="Center"
                  template={inductedTemplateInstance}
                  width={90}
                />
                <ColumnDirective
                  field="timeIn"
                  data-testid="timeIn"
                  format="HH:mm"
                  type="datetime"
                  headerText="Time In"
                  width={90}
                />
                <ColumnDirective
                  field="timeOut"
                  data-testid="timeOut"
                  format="HH:mm"
                  type="datetime"
                  headerText="Time Out"
                  width={90}
                />
                {canAmendDiary
                && (
                  <ColumnDirective
                    allowSorting={false}
                    data-testid="editEntry"
                    allowFiltering={false}
                    template={editTemplateInstance}
                    width={28}
                  />
                )}
              </ColumnsDirective>
              <Inject services={[Paging, Resize]} />
            </GridComponent>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default AuthorisedVisitorsTemporaryWorkers;
